import { Box } from "@mui/material";
import { Config, type SidelinkProps } from "./Config";
import { SidemenuItem, type SidemenuItemProps } from "./components/Item";
import { SidemenuContainer } from "./components/Container";
import { ROUTES } from "@/constants/Routes";
import { useUserStore } from "@/store/useUserStore";
import { BusinessRounded } from "@mui/icons-material";
import { useUserOrganizationStore } from "@/store/useUserOrganizationStore";
import { SidemenuDivider } from "./components/Divider";
import type { UrlObject } from "node:url";
import { SidemenuList } from "./components/List";
import { useAbility } from "@/hooks/useAbility";
import { canCheck } from "@/lib/casl/canCheck";
import type { AppAbility } from "@/lib/casl/types";

function filterItems(
	items: SidelinkProps[],
	ability: AppAbility,
): SidemenuItemProps[] {
	return items.flatMap((item) => {
		if (!canCheck(ability, item.can)) return [];

		if (item.subItems) {
			const visibleSubs = item.subItems
				.filter((sub) => canCheck(ability, sub.can))
				.map(({ value, title, link }) => ({ value, title, link }));

			if (visibleSubs.length === 0) return [];

			return [
				{
					value: item.value,
					title: item.title,
					link: item.link,
					Icon: item.Icon,
					subItems: visibleSubs,
				},
			];
		}

		return [
			{
				value: item.value,
				title: item.title,
				link: item.link,
				Icon: item.Icon,
			},
		];
	});
}

export function Sidemenu() {
	const userId = useUserStore((s) => s.id);
	const ability = useAbility();

	const organizationName = useUserOrganizationStore(
		({ organization }) => organization?.name,
	);

	const organizationUrl: UrlObject | null =
		userId && organizationName
			? {
					pathname: ROUTES.ORGANIZATION.PROFILE,
					query: { userId: userId?.toString() },
				}
			: null;

	const Items = {
		Top: filterItems(Config.Top, ability),
		Editor: filterItems(Config.Editor, ability),
		Bottom: filterItems(Config.Bottom, ability),
	};

	return (
		<SidemenuContainer>
			{organizationUrl && (
				<>
					<SidemenuItem
						Icon={BusinessRounded}
						title='Minha organização'
						value='user-organization'
						key={"user-organization"}
						link={organizationUrl}
					/>
					<SidemenuDivider />
				</>
			)}

			<Box
				sx={{
					overflowY: "auto",
					scrollbarGutter: "stable",
					gap: 1.5,
					display: "flex",
					flexDirection: "column",
					flex: 1,
				}}
			>
				<SidemenuList>
					{Items.Top.map((item) => (
						<SidemenuItem key={item.value} {...item} />
					))}
				</SidemenuList>

				{Items.Editor.length > 0 && (
					<>
						<SidemenuDivider />
						<SidemenuList title='Editor de Questionários'>
							{Items.Editor.map((item) => (
								<SidemenuItem key={item.value} {...item} />
							))}
						</SidemenuList>
					</>
				)}
			</Box>

			<SidemenuDivider />

			<SidemenuList title='Links úteis'>
				{Items.Bottom.map((item) => (
					<SidemenuItem key={item.value} {...item} />
				))}
			</SidemenuList>
		</SidemenuContainer>
	);
}
